import { Building, DollarSign, Calendar, Tag, ChartPie } from "lucide-react";
import { useState, useEffect } from "react";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase";

function Modal({ suscripcion, onClose, onSubmit }) {
  const [nombre, setNombre] = useState("");
  const [precio, setPrecio] = useState("");
  const [moneda, setMoneda] = useState("EUR");
  const [proximoPago, setProximoPago] = useState("");
  const [categoria, setCategoria] = useState("Entretenimiento");
  const [estado, setEstado] = useState("activa");
  const [loading, setLoading] = useState(false);

  // Actualiza los campos cada vez que cambia la suscripción
  useEffect(() => {
    if (suscripcion) {
      setNombre(suscripcion.nombre || "");
      setPrecio(suscripcion.precio || "");
      setMoneda(suscripcion.moneda || "EUR");
      setProximoPago(suscripcion.proximoPago || "");
      setCategoria(suscripcion.categoria || "Entretenimiento");
      setEstado(suscripcion.estado || "activa");
    }
  }, [suscripcion]);

  // Cierra el modal si se hace click fuera del contenido
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Maneja el submit del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const user = auth.currentUser;
    if (!user) {
      alert("No hay usuario autenticado.");
      setLoading(false);
      return;
    }

    const data = {
      nombre,
      precio: Number(precio),
      moneda,
      proximoPago,
      categoria,
      estado,
      uid: user.uid,
    };

    try {
      if (suscripcion && suscripcion.id) {
        // Editar suscripción existente
        await updateDoc(doc(db, "suscripciones", suscripcion.id), data);
        onSubmit({ ...data, id: suscripcion.id });
      } else {
        // Crear nueva suscripción
        const docRef = await addDoc(collection(db, "suscripciones"), {
          ...data,
          createdAt: new Date().toISOString(),
        });
        onSubmit({ ...data, id: docRef.id });
      }
      onClose();
    } catch (error) {
      console.error(error);
      alert("Error al guardar la suscripción");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      {/* Capa oscura */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Contenido del modal */}
      <div className="relative bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {suscripcion && suscripcion.id ? "Editar Suscripción" : "Nueva Suscripción"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Nombre */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4" />
              Nombre del servicio *
            </label>
            <input
              type="text"
              placeholder="ej. Netflix, Spotify, Amazon..."
              className="w-full border-2 border-gray-200 p-3 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          {/* Precio */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4" />
              Precio del servicio *
            </label>
            <div className="flex gap-3">
              <input
                type="number"
                placeholder="0.00"
                step="0.01"
                min="0"
                className="border-2 border-gray-200 p-3 rounded-xl flex-1 focus:border-indigo-500 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={precio}
                onChange={(e) =>
                  setPrecio(e.target.value === "" ? "" : Number(e.target.value))
                }
                disabled={loading}
                required
              />
              <select
                className="border-2 border-gray-200 p-3 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
                disabled={loading}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="MXN">MXN</option>
              </select>
            </div>
          </div>

          {/* Próximo pago */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4" />
              Próximo pago *
            </label>
            <input
              type="date"
              className="w-full border-2 border-gray-200 p-3 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={proximoPago}
              onChange={(e) => setProximoPago(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          {/* Categoría */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Tag className="w-4 h-4" />
              Categoría
            </label>
            <select
              className="w-full border-2 border-gray-200 p-3 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              disabled={loading}
            >
              <option value="Entretenimiento">Entretenimiento</option>
              <option value="Música">Música</option>
              <option value="Productividad">Productividad</option>
              <option value="Desarrollo">Desarrollo</option>
              <option value="Diseño">Diseño</option>
              <option value="Almacenamiento">Almacenamiento</option>
              <option value="Comunicación">Comunicación</option>
              <option value="Fitness">Fitness</option>
              <option value="Educación">Educación</option>
              <option value="Otros">Otros</option>
            </select>
          </div>

          {/* Estado */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <ChartPie className="w-4 h-4" />
              Estado
            </label>
            <select
              className="w-full border-2 border-gray-200 p-3 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              disabled={loading}
            >
              <option value="activa">Activa</option>
              <option value="cancelada">Cancelada</option>
              <option value="prueba">Días de Prueba</option>
            </select>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-400 text-white px-4 py-3 rounded-xl font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Modal;
